import { useEffect, useRef, useState } from "react";
import {
  Box,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Typography,
  Button,
  FormControl,
  Stack,
} from "@mui/material";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import TextInputField from "./textInputField";
import LocationForm from "./locationForm";
import { checkArray, retrieveData, storeData } from "../helper/helper";
import { getPrice } from "../service/api";
import LoaderButton from "./loaderButton";
import Note from "./note";

export default function ParcelInformationForm({
  showQuote,
  setShowQuote,
  onStepIncrement,
  getStepOneData,
  setShowTimer,
}) {
  const [lessThanThreeItems, setLessThanThreeItems] = useState("no");
  const [info, setInfo] = useState({
    price: 0,
    distance: 0,
    acceptMarketing: 0,
  });
  const [distance, setDistance] = useState();
  const [priceLoading, setPriceLoading] = useState(false);
  const [coordinates, setCoordinates] = useState([
    {
      lat: null,
      lng: null,
      address: null,
    },
    {
      lat: null,
      lng: null,
      address: null,
    },
  ]);
  const defaultValues = {
    itemDescription: "",
    peopleInVan: 0,
    // loaderInVan: 0,
  };
  const [showLocationError, setShowLocationError] = useState(false);
  const Schema = yup.object().shape({
    itemDescription: yup.string().required("Required Field"),
    peopleInVan: yup.string().required("Required Field"),
    // loaderInVan: yup.string().required("Required Field"),
  });
  const componentRef = useRef(null);

  const onChangeLessThanThreeItems = (event) => {
    setLessThanThreeItems(event.target.value);
    setShowQuote(false);
  };

  const scrollToComponent = () => {
    if (componentRef.current) {
      componentRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    setValue,
    getValues,
    reset,
  } = useForm({
    defaultValues: defaultValues,
    mode: "onChange",
    resolver: yupResolver(Schema),
  });
  const onStepOneComplete = (data) => {
    if (!checkArray(coordinates)) {
      setShowLocationError(true);
      return;
    }
    if (data) {
      const updatedData = {
        ...data,
        ...info,
        price: info.price.toFixed(2),
        coordinates,
      };
      onStepIncrement();
      getStepOneData(updatedData);
      storeData("stepOneData", { ...updatedData });
    }
  };

  useEffect(() => {
    if (showQuote) {
      scrollToComponent();
    }
  }, [showQuote]);

  const calculatePrice = (pricingData) => {
    const {
      noOfFreeMiles,
      priceOfBaseCharge,
      priceOfPerMile,
      acceptMarketing,
      extraStopCharge,
      threeItems,
    } = pricingData;
    let tempObj = {};
    tempObj.extraStopCharge = extraStopCharge;
    tempObj.noOfStops = coordinates.length;
    tempObj.distance = distance;
    const perMileChargeDistance =
      distance - noOfFreeMiles > 0 ? distance - noOfFreeMiles : 0;
    const withoutMarketing =
      priceOfBaseCharge + perMileChargeDistance * priceOfPerMile;
    tempObj.price =
      withoutMarketing + extraStopCharge * (tempObj.noOfStops - 2);
    tempObj.acceptMarketing = acceptMarketing;
    if (lessThanThreeItems === "yes") {
      tempObj.price = tempObj.price - tempObj.price * (threeItems / 100);
      tempObj.lessThanThreeItems = lessThanThreeItems;
    }
    setInfo(tempObj);
    setShowQuote(true);
  };

  const getPriceValues = async () => {
    if (priceLoading) return;
    setPriceLoading(true);
    try {
      const data = await getPrice();
      if (data) {
        setShowTimer(true);
        calculatePrice(data);
        setPriceLoading(false);
      }
    } catch (err) {
      console.log(err);
      setPriceLoading(false);
    }
  };

  useEffect(() => {
    const storedData = retrieveData("stepOneData");
    if (storedData) {
      setCoordinates(storedData.coordinates);
      reset({ ...storedData });
      setInfo({
        distance: storedData.distance,
        price: storedData.price,
      });
    }
  }, []);

  useEffect(() => {
    setShowQuote(false);
  }, [distance]);

  function calculatePeople(name, value) {
    const formValues = getValues();
    if (name == "loaderInVan") {
      const updatedValue = Number(value) + Number(formValues.peopleInVan);
      if (updatedValue < 3) {
        setValue(name, Number(value));
      }
    } else if (name == "peopleInVan") {
      const updatedValue = Number(value) + Number(formValues.loaderInVan);
      if (updatedValue < 3) {
        setValue(name, Number(value));
      }
    }
  }
  return (
    <div>
      <form>
        <Box sx={{ mt: 3 }}>
          <Typography color={"primary"} fontWeight={500}>
            Where are you moving from / to?<sup>*</sup>
          </Typography>
          <Typography my={1} variant="body2" color="#757575">
            ⓘ Enter full addresses if available, in move order
          </Typography>
          <LocationForm
            coordinates={coordinates}
            setCoordinates={setCoordinates}
            setDistance={setDistance}
          />
          {showLocationError && (
            <FormHelperText sx={{ color: "error.main" }}>
              Location can't be empty
            </FormHelperText>
          )}
        </Box>
        <Box sx={{ mt: 3 }}>
          <Typography color={"primary"} fontWeight={500}>
            What items are you moving?<sup>*</sup>
          </Typography>

          <TextInputField
            controller={{ control: control, errors: errors.itemDescription }}
            name={"itemDescription"}
            minRows={3}
            required
            placeholder={`1x kingsize bedframe
1x kingsize mattress
1x 3 seater sofa
20x medium boxes
10x bin liners
ect...`}
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography fontWeight={500} color={"primary"}>
            Are you moving 3 items (or less)?
          </Typography>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <RadioGroup
              row
              value={lessThanThreeItems}
              onChange={onChangeLessThanThreeItems}
            >
              <FormControlLabel value={"no"} control={<Radio />} label="No" />
              <FormControlLabel value={"yes"} control={<Radio />} label="Yes" />
            </RadioGroup>
          </FormControl>
        </Box>
        <Box sx={{ mt: 3 }}>
          <Typography fontWeight={500} color={"primary"}>
            Passengers to transport in the van during this job?
          </Typography>
          <Typography my={1} variant="body2" color="#757575">
            ⓘ Ride at no extra cost. Must be 12yo> or 135cm> tall
          </Typography>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <Controller
              name={"peopleInVan"}
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange, onBlur } }) => (
                <RadioGroup
                  row
                  value={value}
                  onChange={onChange}
                  // onChange={(e) =>
                  //   calculatePeople("peopleInVan", e.target.value)
                  // }
                >
                  <FormControlLabel value={0} control={<Radio />} label="0" />
                  <FormControlLabel value={1} control={<Radio />} label="1" />
                  <FormControlLabel value={2} control={<Radio />} label="2" />
                </RadioGroup>
              )}
            />
            {errors.peopleInVan && (
              <FormHelperText sx={{ color: "error.main" }}>
                {errors.peopleInVan.message}
              </FormHelperText>
            )}
          </FormControl>
        </Box>
        {/* <Box sx={{ mt: 3 }}>
          <Typography fontWeight={500}>
            Loaders to transport in the van during this job?<sup>*</sup>
          </Typography>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <Controller
              name={"loaderInVan"}
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange, onBlur } }) => (
                <RadioGroup
                  row
                  value={value}
                  onChange={(e) =>
                    calculatePeople("loaderInVan", e.target.value)
                  }
                >
                  <FormControlLabel value={0} control={<Radio />} label="0" />
                  <FormControlLabel value={1} control={<Radio />} label="1" />
                  <FormControlLabel value={2} control={<Radio />} label="2" />
                </RadioGroup>
              )}
            />
            {errors.loaderInVan && (
              <FormHelperText sx={{ color: "error.main" }}>
                {errors.loaderInVan.message}
              </FormHelperText>
            )}
          </FormControl>
        </Box> */}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <LoaderButton
            className={"generateQuote"}
            loading={priceLoading}
            onClick={getPriceValues}
            disabled={
              !isValid ||
              coordinates[coordinates.length - 1].lat === null ||
              !distance
            }
            showLoader={priceLoading}
            buttonText={"DISPLAY QUOTE NOW"}
          />
        </div>

        <div>
          {showQuote && (
            <div>
              {/* <div>
                {info?.distance ? (
                  <h3>Distance: {info?.distance} miles</h3>
                ) : null}
              </div> */}

              <Stack
                mt={2}
                direction={"row"}
                justifyContent={"center"}
                flexWrap={"wrap"}
                gap={2}
                ref={componentRef}
              >
                {/*
                <Stack
                  sx={{
                    justifyContent: "space-between",
                    p: 2,
                    borderRadius: 2,
                    border: "2px solid #1b66b2",
                  }}
                  gap={1}
                >
                  {info?.price ? (
                    <Typography variant="h6" color={"primary"}>
                      Price : £
                      {(
                        info.price -
                        info.price * (info.acceptMarketing / 100)
                      ).toFixed(2)}{" "}
                      &nbsp;
                      <Typography variant="caption">(Inc. Tax)</Typography>
                    </Typography>
                  ) : null}
                  <Typography>Accept Marketing</Typography>
                  <Typography color={"primary"}>
                    {info?.acceptMarketing} % Discount
                  </Typography>
                  <Button
                    onClick={() => {
                      handleSubmit(onStepOneComplete)();
                      storeData("meta", {
                        acceptMarketing: true,
                        price: (
                          info.price -
                          info.price * (info.acceptMarketing / 100)
                        ).toFixed(2),
                      });
                    }}
                    variant="contained"
                    size="small"
                  >
                    Select and book now
                  </Button>
                </Stack>
                */}
                <Stack
                  sx={{
                    justifyContent: "space-between",
                    p: 2,
                    borderRadius: 2,
                    border: "2px solid #1b66b2",
                  }}
                  borderColor={"primary"}
                  gap={1}
                >
                  {info?.price ? (
                    <Typography variant="h6">
                      Price : £{info.price.toFixed(2)}
                      &nbsp;
                      <Typography variant="caption">(Inc. Tax)</Typography>
                    </Typography>
                  ) : null}
                  <Typography>1x Man And Van</Typography>
                  <Typography>+ Assisted Loading</Typography>
                  <Typography>+ Assisted Unloading</Typography>
                  <Button
                    onClick={() => {
                      handleSubmit(onStepOneComplete)();
                      storeData("meta", {
                        acceptMarketing: false,
                      });
                    }}
                    variant="contained"
                    size="small"
//                    sx={{
//                      backgroundColor: "#e5e5ea !important",
//                      color: "#1B66B2 !important",
//                      "&:hover": {
//                        backgroundColor: "#1b66b2 !important",
//                        color: "#FFFFFF !important",
//                      },
//                    }}
                  >
                    Select and book now
                  </Button>
                </Stack>
              </Stack>
              <Note />
              <Typography
                sx={{ marginTop: 2, marginBottom: 2 }}
                variant="body2"
                color="#757575"
              >
                ⓘ Need extra man? let us know in the "Other Info" box at Step 2 of this form...
              </Typography>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
